import { useTranslation } from "react-i18next";
import { BaseInsight, InsightsParams } from "./Insight.types";
import { filter, map } from "lodash";
import { Person } from "@/features/people/graphql/generated";
import {
  getLikelyStrengthInsight,
  getDevelopmentPotentialInsight,
  getUniqueSkillsInsight,
  CompetencyInsightParams,
} from "./getCompetencyInsight";

export interface CompetencyInsight extends BaseInsight {
  type: "likely_strengths" | "development_potential" | "unique_skills";
}

export const useCompetencyInsights = (params: InsightsParams) => {
  const { dimensions, context, person } = params;

  const { t } = useTranslation();

  const dimensionsSortedByScore = filter(
    dimensions,
    (dim) => dim.parentId !== null
  );

  const topDimensions = filter(
    dimensionsSortedByScore,
    (dim) => (dim.score?.value || 0) >= 70
  );
  const bottomDimensions = filter(
    dimensionsSortedByScore,
    (dim) => (dim.score?.value || 0) <= 30
  );

  const getUniqueSkills = (): CompetencyInsight[] => {
    if (context === "user" || context === "person") return [];

    const competencyParams: CompetencyInsightParams = {
      context,
      person,
      defaultPersonId: params.defaultPersonId,
    };

    return dimensionsSortedByScore
      .map((dim) => getUniqueSkillsInsight(dim, competencyParams, t))
      .filter((insight): insight is CompetencyInsight => insight !== null);
  };

  const getLikelyStrengths = (): CompetencyInsight[] => {
    const competencyParams: CompetencyInsightParams = {
      context,
      person,
      defaultPersonId: params.defaultPersonId,
    };

    return map(topDimensions, (dim) =>
      getLikelyStrengthInsight(dim, competencyParams, t)
    ).filter((insight): insight is CompetencyInsight => insight !== null);
  };

  const getDevelopmentPotential = (): CompetencyInsight[] => {
    const competencyParams: CompetencyInsightParams = {
      context,
      person,
      defaultPersonId: params.defaultPersonId,
    };

    return map(bottomDimensions, (dim) =>
      getDevelopmentPotentialInsight(dim, competencyParams, t)
    ).filter((insight): insight is CompetencyInsight => insight !== null);
  };

  return {
    uniqueSkills: getUniqueSkills(),
    likelyStrengths: getLikelyStrengths(),
    developmentPotential: getDevelopmentPotential(),
  };
};
